import { useEffect, useState } from "react";
import { validateUser } from "../../../../actions/auth";
import { pushEventGTM } from "../../../../utils/gtm";

/**
 * Verifies the email-verification token from the link in the email.
 *
 * status: "verifying" → "verified" (user = { userId, role, … }) or "failed".
 */
export const VERIFICATION_STATUS = {
    VERIFYING: "verifying",
    VERIFIED: "verified",
    FAILED: "failed",
};

// Keep the loading state on screen at least this long, so a fast response
// doesn't flash the spinner for a split second before the result.
const MIN_LOADING_MS = 700;

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

// JWT payloads are base64url (- and _ instead of + and /, no padding);
// plain atob() fails on some tokens, so convert first.
const decodeTokenPayload = (token) => {
    const base64Url = token.split(".")[1] || "";
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
};

// validateUser() resolves to the axios response, or [] when the request failed.
const isSuccessfulResponse = (response) => response?.data?.code === 200;

const useEmailVerification = (token) => {
    const [status, setStatus] = useState(VERIFICATION_STATUS.VERIFYING);
    const [user, setUser] = useState(null);

    useEffect(() => {
        // Ignore the result if the user has left the page in the meantime.
        let isActive = true;
        // A new token (same page, different link) starts from the loading state.
        setStatus(VERIFICATION_STATUS.VERIFYING);
        setUser(null);

        const verify = async () => {
            const [response] = await Promise.all([validateUser({ token }), wait(MIN_LOADING_MS)]);
            if (!isActive) return;

            if (!isSuccessfulResponse(response)) {
                setStatus(VERIFICATION_STATUS.FAILED);
                return;
            }

            try {
                const verifiedUser = decodeTokenPayload(token).user;
                pushEventGTM({
                    event: "signup",
                    authentication_method: "email",
                    user_id: verifiedUser.userId,
                });
                setUser(verifiedUser);
                setStatus(VERIFICATION_STATUS.VERIFIED);
            } catch (error) {
                console.error("Email verification: could not read the token", error);
                setStatus(VERIFICATION_STATUS.FAILED);
            }
        };

        verify();
        return () => {
            isActive = false;
        };
    }, [token]);

    return { status, user };
};

export default useEmailVerification;
